/** Generic confirm dialog used for promote / archive / delete-draft. */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: string;
  submitLabel: string;
  onConfirm: () => void | Promise<void>;
  destructive?: boolean;
  pending?: boolean;
  testId?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  submitLabel,
  onConfirm,
  destructive = false,
  pending = false,
  testId,
}: ConfirmDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const submitGuardRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const blocked = pending || submitting;

  async function handleConfirm(): Promise<void> {
    if (submitGuardRef.current || pending) return;
    submitGuardRef.current = true;
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      submitGuardRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && blocked) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">{body}</p>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={blocked}
            data-testid={testId ? `${testId}-cancel` : undefined}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            variant={destructive ? "destructive" : "default"}
            onClick={() => void handleConfirm()}
            disabled={blocked}
            data-testid={testId ? `${testId}-submit` : undefined}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
